import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { getContactLists, ContactList } from '../../firebase/services/listService';
import { getEmailTemplates, EmailTemplate } from '../../firebase/services/emailService';
import { createCampaign, getCampaignById } from '../../firebase/services/campaignService';

/**
 * Component for editing or creating email campaigns
 */
const CampaignEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  // State for form fields
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [fromName, setFromName] = useState('Fallen Leaf Marina');
  const [replyTo, setReplyTo] = useState('info@fallenleafmarina.com');
  const [listId, setListId] = useState('');
  const [templateId, setTemplateId] = useState('');

  // State for contact lists
  const [contactLists, setContactLists] = useState<ContactList[]>([]);
  const [listsLoading, setListsLoading] = useState(true);
  const [listsError, setListsError] = useState<string | null>(null);

  // State for email templates
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);

  // State for saving
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loadingCampaign, setLoadingCampaign] = useState(false);

  // Load contact lists from Firestore
  useEffect(() => {
    const fetchLists = async () => {
      setListsLoading(true);
      setListsError(null);
      try {
        const lists = await getContactLists({ orderBy: 'name', orderDirection: 'asc' });
        setContactLists(lists);
      } catch (err) {
        setListsError('Failed to load contact lists');
        console.error('Error loading contact lists:', err);
      } finally {
        setListsLoading(false);
      }
    };
    fetchLists();
  }, []);

  // Load email templates from Firestore
  useEffect(() => {
    const fetchTemplates = async () => {
      setTemplatesLoading(true);
      setTemplatesError(null);
      try {
        const loadedTemplates = await getEmailTemplates();
        setTemplates(loadedTemplates);
      } catch (err) {
        setTemplatesError('Failed to load email templates');
        console.error('Error loading email templates:', err);
      } finally {
        setTemplatesLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  // Update selected template when templateId changes
  useEffect(() => {
    setSelectedTemplate(templates.find(t => t.id === templateId) || null);
  }, [templateId, templates]);

  // Load campaign data for editing after lists and templates are loaded
  useEffect(() => {
    const loadCampaign = async () => {
      if (isEditMode && id && !listsLoading && !templatesLoading) {
        setLoadingCampaign(true);
        try {
          const campaign = await getCampaignById(id);
          if (campaign) {
            setName(campaign.name || '');
            setSubject(campaign.subject || '');
            setFromName(campaign.fromName || '');
            setReplyTo(campaign.replyToEmail || '');
            setListId(campaign.listIds && campaign.listIds.length > 0 ? campaign.listIds[0] : '');
            setTemplateId(campaign.templateId || '');
          }
        } catch (err) {
          setSaveError('Failed to load campaign');
          console.error('Error loading campaign:', err);
        } finally {
          setLoadingCampaign(false);
        }
      }
    };
    loadCampaign();
  }, [isEditMode, id, listsLoading, templatesLoading]);

  // Handle save action
  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await createCampaign({
        name,
        subject,
        fromName,
        fromEmail: replyTo, // or fromName/fromEmail as needed
        replyToEmail: replyTo,
        listIds: listId ? [listId] : [],
        status: 'draft',
        content: '', // not used, but required by type
        headerEnabled: false,
        footerEnabled: false,
        templateId,
      });
      navigate('../campaigns');
    } catch (err) {
      setSaveError('Failed to save campaign');
      console.error('Error saving campaign:', err);
    } finally {
      setSaving(false);
    }
  };
  
  // Handle send/schedule action
  const handleSend = () => {
    // In a real app, set the campaign to active or schedule it
    console.log('Sending/scheduling campaign', { name, subject, fromName, replyTo, listId, templateId });
    
    // Navigate back to the campaigns list
    navigate('../campaigns');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            sx={{ mr: 2 }}
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('../campaigns')}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            {isEditMode ? 'Edit Campaign' : 'Add Campaign'}
          </Typography>
        </Box>
        <Box>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            sx={{ mr: 2 }}
            disabled={saving || loadingCampaign}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button 
            variant="contained" 
            color="secondary"
            startIcon={<SendIcon />}
            onClick={handleSend}
            disabled={loadingCampaign}
          >
            Send/Schedule
          </Button>
        </Box>
      </Box>
      <Paper sx={{ p: 3 }}>
        {(saveError || loadingCampaign) && (
          <Typography color={loadingCampaign ? 'textSecondary' : 'error'} sx={{ mb: 2 }}>
            {loadingCampaign ? 'Loading campaign...' : saveError}
          </Typography>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Campaign Name"
              fullWidth
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loadingCampaign}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Subject Line"
              fullWidth
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={loadingCampaign}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="From Name"
              fullWidth
              required
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              disabled={loadingCampaign}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Reply-to Email"
              fullWidth
              required
              type="email"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              disabled={loadingCampaign}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Contact List</InputLabel>
              <Select
                value={listId}
                label="Contact List"
                onChange={(e) => setListId(e.target.value)}
                disabled={listsLoading || !!listsError || loadingCampaign}
              >
                {listsLoading && <MenuItem value="" disabled>Loading...</MenuItem>}
                {listsError && <MenuItem value="" disabled>{listsError}</MenuItem>}
                {!listsLoading && !listsError && contactLists.length === 0 && (
                  <MenuItem value="" disabled>No contact lists found</MenuItem>
                )}
                {!listsLoading && !listsError && contactLists.map((list) => (
                  <MenuItem key={list.id} value={list.id}>
                    {list.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Email Template</InputLabel>
              <Select
                value={templateId}
                label="Email Template"
                onChange={(e) => setTemplateId(e.target.value)}
                disabled={templatesLoading || !!templatesError || loadingCampaign}
              >
                {templatesLoading && <MenuItem value="" disabled>Loading...</MenuItem>}
                {templatesError && <MenuItem value="" disabled>{templatesError}</MenuItem>}
                {!templatesLoading && !templatesError && templates.length === 0 && (
                  <MenuItem value="" disabled>No templates found</MenuItem>
                )}
                {!templatesLoading && !templatesError && templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Email Template Preview
            </Typography>
            {selectedTemplate ? (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {selectedTemplate.subject}
                  </Typography>
                  <Box sx={{ border: '1px solid #eee', borderRadius: 1, p: 2, background: '#fafafa' }}>
                    <div dangerouslySetInnerHTML={{ __html: selectedTemplate.body }} />
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a template to preview its content.
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default CampaignEditor; 